'use client';

import React, { useState } from 'react';
import { TextField, Button, Box, Paper, Typography, Grid, Divider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { useEstimator } from '../../context/EstimatorContext';

const BasicInfo = ({ onComplete }) => {
  const navigate = useNavigate();
  const { formData, setFormData } = useEstimator();

  const [localData, setLocalData] = useState({
    date: dayjs(formData.date).isValid() ? formData.date : dayjs().format('YYYY-MM-DD'),
    estimatedBy: formData.estimatedBy || '',
    requestedBy: formData.requestedBy || '',
    projectName: formData.projectName || '',
    projectScope: formData.projectScope || '',
  });

  const isValid =
    !!localData.date &&
    localData.estimatedBy.trim() &&
    localData.requestedBy.trim() &&
    localData.projectName.trim() &&
    localData.projectScope.trim();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (newValue) => {
    if (dayjs(newValue).isValid()) {
      setLocalData((prev) => ({
        ...prev,
        date: dayjs(newValue).format('YYYY-MM-DD'),
      }));
    }
  };

  const handleNext = () => {
    setFormData((prev) => ({ ...prev, ...localData }));
    onComplete();
  };

  return (
    <Box display="flex" justifyContent="center" mt={8} px={2}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 700,
          bgcolor: '#fff',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" align="left">
          Pipeline Estimator
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5} mb={2} align="left">
          Provide the necessary details to start your estimate
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <DatePicker
                label="Date"
                value={dayjs(localData.date)}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    variant: 'outlined',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="estimatedBy"
                value={localData.estimatedBy}
                onChange={handleChange}
                fullWidth
                size="small"
                variant="outlined"
                label="Estimated By"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="requestedBy"
                value={localData.requestedBy}
                onChange={handleChange}
                fullWidth
                size="small"
                variant="outlined"
                label="Requested By"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="projectName"
                value={localData.projectName}
                onChange={handleChange}
                fullWidth
                size="small"
                variant="outlined"
                label="Project Name"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="projectScope"
                value={localData.projectScope}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                size="small"
                variant="outlined"
                label="Project Scope"
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
          <Button variant="contained" disabled={!isValid} onClick={handleNext} sx={{ px: 4, py: 1 }}>
            Next
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BasicInfo;
